import { put } from 'redux-saga/effects';
import axios from 'src/api/Api';
import {
    GET_BATCH_EXPORT_SUCCESS,
    GET_BATCH_EXPORT_FAILURE,
} from './../actions/constants';

const getHeaderValue = (headers, key) => {
    if (!headers) return '';
    return headers[key] || headers[key.toLowerCase()] || '';
};

const getFileNameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition) return null;

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch (e) {
            return utf8Match[1];
        }
    }

    const basicMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return basicMatch?.[1] || null;
};

/**
 * Download file from Blob or ArrayBuffer
 * @param {Blob|ArrayBuffer} fileData - The file content as Blob or ArrayBuffer
 * @param {string} fileName - The name for the downloaded file
 * @param {string} mimeType - The MIME type of the file (default: application/pdf)
 */
const downloadFileFromBlob = (fileData, fileName, mimeType = 'application/pdf') => {
    try {
        let blob;
        
        // Handle different input types
        if (fileData instanceof Blob) {
            blob = fileData;
        } else if (fileData instanceof ArrayBuffer) {
            blob = new Blob([fileData], { type: mimeType });
        } else if (typeof fileData === 'string') {
            // If it's a base64 string, decode it
            try {
                const binaryString = window.atob(fileData);
                const binaryLength = binaryString.length;
                const bytes = new Uint8Array(binaryLength);
                for (let i = 0; i < binaryLength; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }
                blob = new Blob([bytes], { type: mimeType });
            } catch (e) {
                // If atob fails, treat it as plain binary data
                blob = new Blob([fileData], { type: mimeType });
            }
        } else {
            console.error('Unsupported file data type');
            return false;
        }

        // Create download link
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = fileName;

        // Trigger download
        document.body.appendChild(link);
        link.click();

        // Cleanup
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);

        return true;
    } catch (error) {
        console.error('Error downloading file:', error);
        return false;
    }
};

export function* batchExportSaga(action) {
    const baseSiteId = localStorage.getItem('baseSiteId');
    if (!baseSiteId) return;

    let { batchID, setNumber } = action.payload;

    // Support alternative property names (prioritize the ones actually used in the API)
    if (!batchID) {
        batchID = action.payload.batchId || action.payload.batchNo || action.payload.batchNumber;
    }
    if (!setNumber) {
        setNumber = action.payload.setNo;
    }

    if (!batchID || !setNumber) {
        yield put({
            type: GET_BATCH_EXPORT_FAILURE,
            payload: {
                error: `Batch ID and Set Number are required. Received: batchID=${batchID}, setNumber=${setNumber}`,
            },
        });
        return;
    }

    try {
        // API endpoint path
        const url = `/occ/v2/${baseSiteId}/dashboard/set/export`;
        
        // API call with query parameters
        // Get response as text (CSV data)
        const response = yield axios.get(url, {
            params: {
                batch: batchID,
                set: setNumber,
            },
            responseType: 'arraybuffer',
        });

        const contentType = getHeaderValue(response.headers, 'content-type')
            || 'application/octet-stream';
        const contentDisposition = getHeaderValue(response.headers, 'content-disposition');

        // Prefer filename provided by API; fallback keeps current naming pattern.
        const timestamp = new Date().toISOString().split('T')[0];
        const fallbackFileName = `Batch_${batchID}_Set_${setNumber}_${timestamp}.xls`;
        const fileName = getFileNameFromContentDisposition(contentDisposition) || fallbackFileName;

        const downloadSuccess = downloadFileFromBlob(response.data, fileName, contentType);

        if (downloadSuccess) {
            yield put({
                type: GET_BATCH_EXPORT_SUCCESS,
                payload: {
                    fileName,
                },
            });
        } else {
            yield put({
                type: GET_BATCH_EXPORT_FAILURE,
                payload: {
                    error: 'Failed to process file for download',
                },
            });
        }
    } catch (error) {
        console.error('Batch export error:', error);
        yield put({
            type: GET_BATCH_EXPORT_FAILURE,
            payload: {
                error: error?.message || 'Failed to export batch',
            },
        });
    }
}
